import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { fetchProductReviews, imgUrl } from "../api";
import ReviewStars from "./ReviewStars";
import ReviewForm from "./ReviewForm";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { month: "short", year: "numeric" });
  } catch {
    return "";
  }
};

const ReviewsSection = ({ productId, productSlug }) => {
  const [data, setData] = useState({ count: 0, average: 0, distribution: {}, reviews: [] });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const idForApi = productSlug || productId;

  useEffect(() => {
    if (!idForApi) return;
    let cancelled = false;
    setLoading(true);
    fetchProductReviews(idForApi)
      .then((r) => { if (!cancelled) setData(r); })
      .catch(() => { if (!cancelled) setData({ count: 0, average: 0, distribution: {}, reviews: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [idForApi, refreshKey]);

  return (
    <section id="reviews" className="mt-20 lg:mt-28" data-testid="reviews-section">
      <div className="border-t border-[var(--sand)] pt-14 lg:pt-20">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <p className="text-[11px] tracking-[0.3em] uppercase text-[var(--copper)] mb-4">Studio Reviews</p>
            <h2 className="font-serif-display text-4xl lg:text-5xl leading-[1.08] text-[var(--espresso)]">
              {data.count === 0 ? (
                <>Be the first to <em className="italic text-[var(--copper)]">share</em>.</>
              ) : (
                <>What customers <em className="italic text-[var(--copper)]">say</em>.</>
              )}
            </h2>
            {data.count > 0 && (
              <div className="mt-4 flex items-center gap-4">
                <ReviewStars value={data.average} size={20} />
                <span className="text-sm text-[var(--espresso)]/70">
                  <strong className="text-[var(--espresso)]">{data.average.toFixed(1)}</strong> · {data.count} {data.count === 1 ? "review" : "reviews"}
                </span>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            data-testid="reviews-write-cta"
            className="vc-btn-ghost inline-flex items-center h-12 px-8 text-[11px] tracking-[0.25em] uppercase"
          >
            {showForm ? "Close" : "Write a review"}
          </button>
        </div>

        {showForm && (
          <div className="mb-14">
            <ReviewForm productId={productId} onDone={() => { setShowForm(false); setRefreshKey((k) => k + 1); }} />
          </div>
        )}

        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[var(--copper)]" /></div>
        ) : data.reviews.length === 0 ? (
          !showForm && (
            <p className="text-[var(--espresso)]/60 py-8">
              No reviews yet — share yours and help the next customer see this piece in a real home.
            </p>
          )
        ) : (
          <div className="space-y-10">
            {data.reviews.map((r) => (
              <article key={r.id} data-testid={`review-${r.id}`} className="pb-10 border-b border-[var(--sand)] last:border-0 last:pb-0">
                <div className="flex items-center gap-4 mb-3">
                  <ReviewStars value={r.rating} size={16} />
                  <span className="text-xs tracking-[0.14em] uppercase text-[var(--espresso)]/50">
                    {r.name} · {fmtDate(r.approved_at || r.created_at)}
                  </span>
                </div>
                {r.title && (
                  <h3 className="font-serif-display text-xl text-[var(--espresso)] mb-2">{r.title}</h3>
                )}
                <p className="text-[var(--espresso)]/80 leading-[1.9] whitespace-pre-line">{r.text}</p>
                {r.photos && r.photos.length > 0 && (
                  <div className="mt-5 flex flex-wrap gap-3">
                    {r.photos.map((src) => (
                      <a key={src} href={imgUrl(src)} target="_blank" rel="noopener noreferrer" className="w-28 h-28 bg-[var(--sand)] overflow-hidden">
                        <img src={imgUrl(src)} alt="Customer photo" loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                      </a>
                    ))}
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ReviewsSection;
